"use client";
import { useEffect, useState } from "react";
import { MobileNav } from "./mobile-nav";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { BookOpen, User, LogOut, Zap } from "lucide-react";
import { useAuth } from "@/contexts/useAuth";
import { Link } from "react-router-dom";
import { useCallback, useRef } from "react";

export function SiteHeader() {
	const { user, isAuthenticated, logout } = useAuth();

	const [activeDropdown, setActiveDropdown] = useState(null);
	const [showAboutDropdown, setShowAboutDropdown] = useState(false);
	const [isMobile, setIsMobile] = useState(false);
	const aboutRef = useRef(null);

	// Fix initials (charAt(-1) was invalid)
	const getInitials = () => {
		if (!user?.email) return "?";
		const email = user.email.trim();
		const parts = email
			.split("@")[0]
			.split(/[.\-_]/)
			.filter(Boolean);
		if (parts.length >= 2) {
			return (parts[0][0] + parts[1][0]).toUpperCase();
		}
		return (email[0] + (email[email.length - 1] || "")).toUpperCase();
	};

	// Responsive check
	useEffect(() => {
		const mq = window.matchMedia("(max-width: 767px)");
		const update = () => setIsMobile(mq.matches);
		update();
		mq.addEventListener("change", update);
		return () => mq.removeEventListener("change", update);
	}, []);

	// Close on outside click
	useEffect(() => {
		const handleClick = (e) => {
			if (
				aboutRef.current &&
				!aboutRef.current.contains(e.target) &&
				activeDropdown === "about"
			) {
				setActiveDropdown(null);
				setShowAboutDropdown(false);
			}
		};
		document.addEventListener("mousedown", handleClick);
		return () => document.removeEventListener("mousedown", handleClick);
	}, [activeDropdown]);

	// Close on ESC
	useEffect(() => {
		const esc = (e) => {
			if (e.key === "Escape") {
				setActiveDropdown(null);
				setShowAboutDropdown(false);
			}
		};
		document.addEventListener("keydown", esc);
		return () => document.removeEventListener("keydown", esc);
	}, []);

	const handleDropdownClick = useCallback(
		(name, e) => {
			e.preventDefault();
			if (activeDropdown === name) {
				setActiveDropdown(null);
				setShowAboutDropdown(false);
			} else {
				setActiveDropdown(name);
				if (name === "about") setShowAboutDropdown(true);
			}
		},
		[activeDropdown]
	);

	const handleLinkClick = () => {
		setActiveDropdown(null);
		setShowAboutDropdown(false);
	};

	return (
		<header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
			<div className="container flex h-16 items-center justify-between">
				<div className="flex items-center gap-2">
					<Link to="/" className="w-10 space-x-2 header__logo-link">
						<img
							src="/ighota-logo.png"
							alt="ighota-logo"
							draggable={false}
							className="header__logo"
						/>
					</Link>
				</div>

				<nav className="hidden md:flex md:gap-6 lg:gap-10 items-center">
					{/* Custom About Us dropdown */}
					<div
						ref={aboutRef}
						className="relative"
						onMouseEnter={() =>
							!isMobile && setShowAboutDropdown(true)
						}
						onMouseLeave={() =>
							!isMobile && setShowAboutDropdown(false)
						}>
						<button
							onClick={(e) => handleDropdownClick("about", e)}
							className={`px-3 py-2 text-sm rounded-md font-medium transition-colors hover:bg-accent ${
								activeDropdown === "about" ||
								(!isMobile && showAboutDropdown)
									? "bg-accent"
									: ""
							}`}>
							About Us
						</button>
						{((!isMobile && showAboutDropdown) ||
							activeDropdown === "about") && (
							<div className="absolute left-0 mt-2 w-48 rounded-md border bg-popover shadow-md p-2 animate-in fade-in-0 zoom-in-95 dropdown">
								<Link
									to="/team"
									onClick={handleLinkClick}
									className="block px-2 py-1.5 rounded text-sm hover:bg-accent">
									Our Team
								</Link>
								<Link
									to="/mission"
									onClick={handleLinkClick}
									className="block px-2 py-1.5 rounded text-sm hover:bg-accent">
									Our Mission
								</Link>
							</div>
						)}
					</div>

					<Button variant="ghost" asChild>
						<Link to="/dashboard/courses">Courses</Link>
					</Button>
					<Button variant="ghost" asChild>
						<Link to="/partners">Partners</Link>
					</Button>
					<Button variant="ghost" asChild>
						<Link to="/contact">Contact</Link>
					</Button>
					<Button variant="ghost" asChild>
						<Link
							to="https://www.gofundme.com/f/empower-nigerian-kids-with-virtual-learning-join-us"
							target="_blank"
							rel="noopener noreferrer">
							Donate
						</Link>
					</Button>
				</nav>

				<div className="flex items-center gap-4">
					<Button
						asChild
						className="hidden gap-1.5 rounded-full bg-yellow-500 text-black shadow-sm hover:bg-yellow-600 hover:shadow-md sm:inline-flex">
						<Link to="/benchmark-test">
							<Zap className="h-4 w-4 fill-current" />
							Benchmark Test
						</Link>
					</Button>
					{!isAuthenticated ? (
						<div className="flex items-center gap-2">
							<Button variant="ghost" asChild>
								<Link to="/auth/login">Login</Link>
							</Button>
							<Button asChild>
								<Link to="/auth/register">Sign Up</Link>
							</Button>
						</div>
					) : (
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Avatar className="h-8 w-8 cursor-pointer">
									<AvatarFallback>
										{getInitials()}
									</AvatarFallback>
								</Avatar>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end" className="w-56">
								<DropdownMenuLabel className="font-normal">
									<div className="flex flex-col space-y-1">
										<p className="text-sm font-medium leading-none">
											Account
										</p>
										<p className="text-xs leading-none text-muted-foreground">
											{user?.email}
										</p>
									</div>
								</DropdownMenuLabel>
								<DropdownMenuSeparator />
								<DropdownMenuItem asChild>
									<Link to="/dashboard/profile">
										<User className="mr-2 h-4 w-4" />
										Profile
									</Link>
								</DropdownMenuItem>
								<DropdownMenuItem asChild>
									<Link to="/dashboard/courses">
										<BookOpen className="mr-2 h-4 w-4" />
										My Courses
									</Link>
								</DropdownMenuItem>
								<DropdownMenuSeparator />
								<DropdownMenuItem onClick={logout}>
									<LogOut className="mr-2 h-4 w-4" />
									Logout
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					)}
					<MobileNav />
				</div>
			</div>
		</header>
	);
}
